import { Map, TileLayer, VectorLayer, Extent } from "maptalks"
import _ from "lodash"
import { feature as turfFeature, featureCollection } from "@turf/helpers"
import turfCenter from "@turf/center"
import bbox from "@turf/bbox"

import * as THREE from "three"
import { ThreeLayer } from "maptalks.three"

import {
  styledFeatureGenerator,
  getExtrudeConfig,
  getExtrudeConfigByFeature,
} from "./utils/createElements"

import {
  getBearingBetweenPoints,
  getSuitablyValueBetweenBearings,
} from "./utils/math"
import {
  DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
  DEFAULT_BASEMAP,
  LAYERS,
  LAYER_OPTIONS,
} from "./constants"
import TWEEN from "@tweenjs/tween.js"
import { Marker3d } from "./object3d/Marker3d"

const defaultOptions = {
  center: [100.5017051, 13.7572619],
  zoom: 18.5,
  bearing: 0,
  pixelRatio: 1,
}

const CLICK_TOLERANCE = 20
export class IndoorMap {
  //TODO: refac functions; let them do only 1 thing in a function

  /** Note: "#" means private variables */
  #defaultCenter = defaultOptions.center
  #defaultZoom = defaultOptions.zoom
  #defaultBearing = defaultOptions.bearing
  #pixelRatio = defaultOptions.pixelRatio
  #styler = null
  #featuresInitted = false
  #elementsLoaded = false
  #elements = {}
  #features = []
  #ordinals = []
  #mapTheme = {}
  #billboards = []
  #marker3DObjects = []
  #billboardObjects = []
  #mapDecorations = []
  #groundLabels = []
  #groundObjects = []
  #venueObjects = []
  #glbObjects = []
  #objects = []
  #highlightElementIds = []
  #highlightObjectIds = []
  #touchStartTarget = null
  #touchStartPoint = null

  #userLocationGeometry = null
  #userLocationElement = null

  #lastUserLocationGeometries = []
  #lastUserLocationElements = []

  #zoomLevel = defaultOptions.zoom

  constructor(elementId, options) {
    const {
      pixelRatio,
      defaultCenter,
      defaultBearing,
      defaultZoom,
      mapTheme,
      onMapReady,
      onModelReady,
      onMapLoading,
      onClickElement,
      mapConfig,
      minZoom,
      maxZoom,
    } = _.merge({}, defaultOptions, options)
    // To be moved to config

    const spatialReference = {
      projection: "EPSG:3857",
      resolutions: (function () {
        const resolutions = []
        const d = 2 * 6378137 * Math.PI
        for (let i = 0; i < maxZoom; i++) {
          resolutions[i] = d / (256 * Math.pow(2, i))
        }
        return resolutions
      })(),
    }

    this.#defaultCenter = defaultCenter
    this.#defaultZoom = defaultZoom
    this.#zoomLevel = defaultZoom
    this.#defaultBearing = defaultBearing
    this.#pixelRatio = pixelRatio

    this.map = new Map(elementId, {
      attribution: false,
      center: defaultCenter,
      spatialReference,
      zoom: defaultZoom,
      minZoom,
      maxZoom,
      baseLayer: this.#createBaseLayer(_.get(mapConfig, "basemap", {})),
      layers: [],
    })
    this.map.setDevicePixelRatio(this.#pixelRatio)
    this.#createLayers()
    this.mapTheme = mapTheme
    this.mapConfig = mapConfig

    this.onMapReady = onMapReady
    this.onMapLoading = onMapLoading
    this.onModelReady = onModelReady
    this.onClickElement = onClickElement
    this.showVenueObject = false
    // Bind events
    this.map.on("zooming", this.handleMapZooming)
    this.map.on("click", this.handleMapClick)
  }

  // Builds the basemap tile layer from config, merged over DEFAULT_BASEMAP.
  // Returns undefined when disabled, so the map renders with no basemap at all.
  #createBaseLayer(basemapConfig) {
    const {
      enabled,
      url_template,
      subdomains,
      opacity,
      css_filter,
      max_available_zoom,
    } = _.merge({}, DEFAULT_BASEMAP, basemapConfig)

    if (enabled === false) return undefined

    return new TileLayer("base", {
      urlTemplate: url_template,
      ...(subdomains?.length ? { subdomains } : {}),
      opacity,
      maxAvailableZoom: max_available_zoom,
      // An empty cssFilter would set ctx.filter = "" instead of leaving it
      // untouched, so only pass it through when there is one.
      ...(css_filter ? { cssFilter: css_filter } : {}),
    })
  }

  /**
   * Events
   */

  handleMapZooming = ({ to }) => {
    this.#zoomLevel = to
  }

  handleMapClick = ({ coordinate }) => {
    const { x, y } = coordinate
    console.log(`Coordinates: x: ${x} y: ${y}`)
  }

  handleMapTouchEnd = ({ containerPoint }) => {
    const { x, y } = containerPoint
    const { x: touchStartX, y: touchStartY } = this.#touchStartPoint

    const dis = Math.hypot(x - touchStartX, y - touchStartY)
    this.map.off("touchend", this.handleMapTouchEnd)

    if (dis > 0 && dis < CLICK_TOLERANCE) {
      console.log("Forgiving click", this.#touchStartTarget.properties.id)
      this.onClickElement({ target: this.#touchStartTarget })
    }

    this.#touchStartTarget = null
    this.#touchStartPoint = null
  }

  handleSetupForgivingClick = (element) => {
    element.on("touchstart", (e) => {
      this.#touchStartTarget = e.target
      this.#touchStartPoint = e.containerPoint
      this.map.on("touchend", this.handleMapTouchEnd)
    })
  }

  /**
   * Getters & Setters
   */
  get elementsLoaded() {
    return this.#elementsLoaded
  }

  set mapTheme(value) {
    this.#mapTheme = value
    this.#styler = styledFeatureGenerator(this.#mapTheme)
  }

  get ordinals() {
    return this.#ordinals || []
  }

  set ordinals(value) {
    if (!Array.isArray(value)) throw new Error("ordinals must be Array")
    this.#ordinals = value
  }

  set billboards(value) {
    this.#billboards = value
  }

  set mapDecorations(value) {
    this.#mapDecorations = value
  }

  set groundLabels(value) {
    this.#groundLabels = value
  }

  set features(value) {
    /**
     * Setup with features
     * -------------------
     * 1. Create ordinals
     * 2. Init Layers (based on ordinals)
     * 3. Create elements
     */
    if (value.length === 0 || !this.#mapTheme) {
      return
    }

    this.#features = value

    this.ordinals = _(this.#features)
      .filter((f) => f.feature_type === "level")
      .map((f) => f.properties.ordinal)
      .sort()
      .uniq()
      .value()

    if (typeof this.onMapLoading === "function") this.onMapLoading()

    if (this.#featuresInitted) {
      this.#clearElements()
      this.#createElements()
      return
    }
    this.#initMapElements()
  }

  /**
   * Private internal methods
   */

  #clearElements() {
    // TOOD: Find a better way to handle the locations
    LAYERS.filter(
      (key) => key !== "user_location",
      "last_user_location"
    ).forEach((layerKey) => {
      this.#clearAllElementOnLayerByName(layerKey)
    })
    const scene = this.threeLayer.getScene()
    if (scene) {
      scene.children = scene.children.filter(
        (children) => children instanceof THREE.PerspectiveCamera
      )
    }
    this.render()
  }

  #createLayers() {
    const map = this.map
    // Clear Existing Layers
    if (!this.ordinals || this.ordinals.length === 0) return
    // Create Layers
    LAYERS.forEach((layerKey) => {
      const layerID = layerKey
      const option = LAYER_OPTIONS[layerKey]
      const layer = new VectorLayer(layerID, option)
      map.addLayer(layer)
    })

    // ThreeJS Layer
    const threeLayer = new ThreeLayer("t_building", {
      forceRenderOnMoving: true,
      forceRenderOnRotating: true,
      animation: true,
      hitDetect: false,
    })
    this.threeLayer = threeLayer
    threeLayer.addTo(this.map)
  }
  #initMapElements() {
    this.#createLayers()
    this.threeLayer.prepareToDraw = async (gl, scene, camera) => {
      await this.#createElements()
    }
  }
  async #createElements() {
    const {
      // 2D
      createVenue,
      createLevel,
      createUnit,
      createAmenity,
      createOpening,
      createSection,
      createSectionMarker,
      createOccupant,
      createFixture,
      createKiosk,
      createDecoration,

      // 3D
      create3DFootprint,
      create3DGroundLabel,
      create3DBillboard,
      create3DUnit,
      createVenue3DModel,
      createExtrudedUnit,
      create3DFixture,
      createExtrudedKiosk,
      create3DAmenityMarker,
      create3DOccupantAmenityMarker,
      createExtrudedSection,
      // Light
      createAmbientLight,
      createDirectionalLight,
    } = this.#styler

    let elements = {}
    let object3ds = []

    const scene = this.threeLayer.getScene()
    if (scene) {
      const {
        ambientLight: ambientLightConfig = {},
        directionalLight: directionalLightConfig = {},
      } = _.get(this.mapConfig, "light", {})

      const ambientLight = createAmbientLight(ambientLightConfig)
      scene.add(ambientLight)

      const light = createDirectionalLight(directionalLightConfig)
      scene.add(light)
    }
    for (const feature of this.#features) {
      try {
        const { feature_type: featureType, properties, id } = feature
        const layer = this.map.getLayer(featureType)
        let geometry
        const category = _.get(feature, "properties.category")

        const extrudeConfig = _.get(this.mapConfig, "extrude")

        const featureExtrudeConfig = getExtrudeConfig(
          extrudeConfig,
          featureType,
          category
        )

        switch (featureType) {
          case "venue": {
            geometry = createVenue(feature).addTo(layer)
            // Add 3D Model
            const models = await createVenue3DModel(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.onClickElement)
              model.hide()
              this.threeLayer.addMesh(model)
              this.#venueObjects.push(model)
            })
            break
          }
          case "level": {
            geometry = createLevel(feature).addTo(layer)
            break
          }
          case "unit": {
            geometry = createUnit(feature)
              ?.on("click", this.onClickElement)
              .addTo(layer)

            const models = await create3DUnit(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.onClickElement)
              // Hide model at first to prevent model blinked appear before hide by ordinal
              model.hide()
              this.threeLayer.addMesh(model)
              this.#glbObjects.push(model)
            })
            const extrudedUnits = createExtrudedUnit(
              feature,
              this.threeLayer,
              featureExtrudeConfig
            )

            if (extrudedUnits) {
              extrudedUnits.on("click", this.onClickElement)
              // Forgiving Click
              this.handleSetupForgivingClick(extrudedUnits)
              this.threeLayer.addMesh(extrudedUnits)
              object3ds.push(extrudedUnits)
            }

            break
          }
          case "amenity": {
            if (feature.properties.is_featured) {
              const billboardObj = create3DBillboard(feature, this.threeLayer)
              billboardObj?.on("click", this.onClickElement)
              billboardObj?.hide()
              this.threeLayer.addMesh(billboardObj)
              this.#billboardObjects.push(billboardObj)
              break
            }

            if (extrudeConfig) {
              const marker3d = create3DAmenityMarker(
                feature,
                this.threeLayer,
                extrudeConfig
              )?.on("click", this.onClickElement)

              this.threeLayer.addMesh(marker3d)
              this.#marker3DObjects.push(marker3d)
            } else {
              geometry = createAmenity(feature)?.on(
                "click",
                this.onClickElement
              )
              geometry.addTo(layer)
            }
            break
          }
          case "opening": {
            geometry = createOpening(feature).addTo(layer)
            break
          }
          case "section": {
            if (!featureExtrudeConfig) {
              geometry = createSection(feature)
                ?.on("click", this.onClickElement)
                .addTo(layer)

              const sectionMarkerLayer = this.map.getLayer("section.marker")
              const sectionMarker = createSectionMarker(feature, extrudeConfig)
                .on("click", this.onClickElement)
                .addTo(sectionMarkerLayer)
              if (id && sectionMarker) {
                elements[`${id}-marker`] = {
                  geometry: sectionMarker,
                  properties,
                  featureType,
                  feature,
                }
              }
            } else {
              const locatedLevel = feature?.properties?.level
              const levelExtrudeConfig = getExtrudeConfigByFeature(
                extrudeConfig,
                locatedLevel
              )

              const levelHeight = levelExtrudeConfig?.height ?? 0
              const option = {
                ...featureExtrudeConfig,
                altitude: levelHeight,
              }

              const extrudedSection = createExtrudedSection(
                feature,
                this.threeLayer,
                option
              )
              extrudedSection.on("click", this.onClickElement)

              this.handleSetupForgivingClick(extrudedSection)

              object3ds.push(extrudedSection)
            }
            break
          }
          case "occupant": {
            switch (category) {
              // Create only marker if it is amenity occupant
              case "currencyexchange":
                const markerFeature = {
                  ...feature,
                  geometry: feature.properties?.anchor?.geometry,
                }
                const amenityLayer = this.map.getLayer(`amenity`)

                if (extrudeConfig) {
                  const marker3d = create3DOccupantAmenityMarker(
                    markerFeature,
                    this.threeLayer,
                    extrudeConfig
                  )?.on("click", this.onClickElement)
                  this.threeLayer.addMesh(marker3d)
                  this.#marker3DObjects.push(marker3d)
                } else {
                  const marker = createAmenity(markerFeature)
                    ?.on("click", this.onClickElement)
                    .addTo(amenityLayer)
                  geometry = marker
                }
                break
              default:
                geometry = createOccupant(feature, extrudeConfig)?.addTo(layer)
            }
            break
          }
          case "fixture": {
            geometry = createFixture(feature)?.addTo(layer)
            const models = await create3DFixture(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.onClickElement)
              model.hide()
              this.threeLayer.addMesh(model)
              this.#glbObjects.push(model)
            })

            break
          }
          case "kiosk": {
            geometry = createKiosk(feature)
              ?.on("click", this.onClickElement)
              .addTo(layer)
            const extrudedkiosk = createExtrudedKiosk(
              feature,
              this.threeLayer,
              featureExtrudeConfig
            )
            if (extrudedkiosk) {
              extrudedkiosk.on("click", this.onClickElement)
              this.threeLayer.addMesh(extrudedkiosk)
              object3ds.push(extrudedkiosk)
            }
            break
          }
          case "footprint": {
            // Added 3D Footprint
            const objects = await create3DFootprint(
              feature,
              this.threeLayer,
              featureExtrudeConfig
            )
            objects.forEach((object) => {
              object.on("click", () => {
                const {
                  geometry: { coordinates },
                } = turfCenter(feature)
                const zoom = this.#defaultZoom + 1
                this.flyToAndZoomIn(coordinates, { zoom, pitch: 45 })
              })
              this.threeLayer.addMesh(object)
              object3ds.push(object)
              this.#objects.push(object)
            })

            // Add Footprint Marker
            if (feature.properties.logo) {
              const footprintMarker = create3DBillboard(
                feature,
                this.threeLayer
              )
              this.threeLayer.addMesh(footprintMarker)
              this.#billboardObjects.push(footprintMarker)
            }

            break
          }
          default:
            break
        }
        if (!id) throw new Error(`Property is missing ID, ${id}`)
        elements[id] = { geometry, properties, featureType, feature }
      } catch (err) {
        console.log(err, {
          feature,
          layer: `${feature.feature_type}`,
        })
      }
    }

    this.#groundLabels.forEach((label) => {
      const text = label.properties.name
      try {
        const groundLabel = create3DGroundLabel(label, this.threeLayer)
        groundLabel.hide()
        this.threeLayer.addMesh(groundLabel)
        this.#groundObjects.push(groundLabel)
      } catch (error) {
        console.log("error creating ground label for ", text)
      }
    })

    this.#mapDecorations.forEach((decoration) => {
      try {
        const { id, geometry, properties } = decoration
        const decorationLayer = this.map.getLayer(
          `${properties.ordinal}_decoration`
        )
        const createdDecoration = createDecoration(geometry, {
          id,
          ...properties,
        }).addTo(decorationLayer)
        elements[id] = { geometry: createdDecoration, properties }
      } catch (error) {
        console.log(
          "error creating decoration for ",
          decoration.properties.name
        )
      }
    })

    // Initiate Canvas render
    this.render()
    this.#elements = elements
    this.#elementsLoaded = true
    this.#featuresInitted = true
    if (typeof this.onMapReady === "function") this.onMapReady()
    if (typeof this.onModelReady === "function") this.onModelReady()
  }

  #clearAllElementOnLayerByName = (layerName) => {
    const layer = this.map.getLayer(layerName)
    if (layer) layer.clear()
  }

  #showGeometriesByOrdinal(ordinal) {
    _(this.#elements)
      .filter((element) => !!_.get(element, "geometry"))
      .forEach(({ feature_type, geometry, properties }) => {
        ordinal === null ||
        properties?.ordinal === ordinal ||
        feature_type === "venue"
          ? geometry.show()
          : geometry.hide()
      })
  }

  // TODO : Recheck If This one is still in use
  #showLayerByName = (layerName) => {
    const layer = this.map.getLayer(layerName)
    layer ? layer?.show() : layer?.hide()
  }
  #showThreeObjectByOrdinal = (ordinal) => {
    const objects = this.threeLayer?.getBaseObjects() || []
    objects?.forEach((baseObject) => {
      const objectOrdinal = _.get(baseObject, "properties.ordinal")
      if (_.isNil(objectOrdinal)) return
      const show = Boolean(objectOrdinal === ordinal)
      show ? baseObject.show() : baseObject.hide()
    })
  }

  /**
   * Change Level & animate to path / geometry / view / etc.
   * ================================== */
  changeLevelByOrdinal(ordinal) {
    this.#showGeometriesByOrdinal(ordinal) // show / hide geometry on the ordinal
    this.#showThreeObjectByOrdinal(ordinal)
  }

  #animateflyTo(viewOptions = {}, options = {}, callbackOption) {
    const { start, end } = {
      start: () => {},
      end: () => {},
      ...callbackOption,
    }
    this.map.flyTo(viewOptions, options, (frame) => {
      if (frame.state.playState === "running" && frame.state.progress === 0)
        start(frame)
      if (frame.state.playState === "finished") end(frame)
    })
  }

  getBearing = () => this.map.getBearing()

  getFeatureExtent = (feature) => {
    const featureBbox = bbox(feature)
    return new Extent(...featureBbox)
  }

  getExtentCenter = (extent) => {
    return extent.getCenter()
  }

  getExtentZoom = (extent, options = {}) => {
    const { isFraction = false } = options
    return this.map.getFitZoom(extent, isFraction)
  }

  flyTo = (center, options = {}) => {
    const {
      zoom = this.#defaultZoom,
      pitch = 60,
      duration = 600,
      delay = 2000,
      ease = "out",
      bearing = this.getBearing(),
    } = options
    this.#animateflyTo(
      {
        center,
        zoom,
        pitch,
        bearing,
      },
      { delay, duration, ease }
    )
  }

  flyToPath = (
    path,
    options = { duration: 800, easing: "in-out", pitch: 60, zoomOffset: 0.25 }
  ) => {
    try {
      const { zoomOffset, ...restOptions } = options
      const currentBearing = this.map.getBearing()
      const bearingBetweenPoints = getBearingBetweenPoints(
        path.getFirstCoordinate(),
        path.getLastCoordinate()
      )
      const targetBearing = getSuitablyValueBetweenBearings(
        bearingBetweenPoints,
        currentBearing
      )
      const extent = path.getExtent()
      const zoom = this.getExtentZoom(extent) - zoomOffset
      const center = this.getExtentCenter(extent)
      this.flyTo(center, {
        pitch: 60,
        bearing: targetBearing,
        zoom,
        ...restOptions,
      })
    } catch (error) {
      console.log("error in fly to path")
    }
  }
  // Subject to be removed
  flyToAndZoomIn = (centerPoint, options = {}) => {
    const {
      zoom = this.#defaultZoom + 3,
      pitch = 60,
      duration = 600,
      delay = 2000,
      ease = "out",
    } = options
    this.#animateflyTo(
      {
        center: centerPoint,
        zoom,
        pitch,
      },
      { delay, duration, ease }
    )
  }

  flyToOverviewStepPath = (path) => {
    const currentBearing = this.map.getBearing()
    const bearing = getSuitablyValueBetweenBearings(
      this.#defaultBearing,
      currentBearing
    )
    this.flyToPath(path, {
      pitch: 0,
      bearing,
      duration: 600,
      easing: "out",
      zoomOffset: 0.65,
    })
  }

  /**
   * Hilighting Elements
   * ========================================= */

  getElementByIds(targetElementIds = []) {
    const elements = targetElementIds
      .map((elemId) => this.#elements[elemId])
      .filter((elem) => elem)

    if (!elements.length) return

    return featureCollection(
      elements.map(({ feature }) => {
        const { geometry } = feature
        if (feature.feature_type === "occupant")
          return turfFeature(feature?.properties?.anchor?.geometry)
        return turfFeature(geometry)
      })
    )
  }

  // TODO: To consider if we should use setter `set hilightElementIds` instead?
  setHighlightElementIds(targetElementIds) {
    this.setHighlightedObject(targetElementIds)
    return this.setHighlight2DElementIds(targetElementIds)
  }
  setHighlight2DElementIds(targetElementIds, options = {}) {
    const { defaultMarker, symbolSet } = _.merge(
      {},
      DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
      options
    )

    const {
      createMarker,
      getHilighPolygonalSymbol,
      getHighlightMarkerSymbol,
      createHighlightOccupantMarker,
      getElementSymbol,
    } = this.#styler

    const extrudeConfig = _.get(this.mapConfig, "extrude")

    /**
     * Hilight new elements
     */
    const elementToHilights = targetElementIds
      .map((elemId) => this.#elements[elemId])
      .filter((elem) => elem)

    elementToHilights.forEach((elem) => {
      const { feature, geometry } = elem
      if (!geometry || !feature) return

      const hilightLayer = this.map.getLayer("hilight")
      if (!hilightLayer) return

      const defaultSymbol = getHilighPolygonalSymbol(geometry.type)
      const definedSymbol = symbolSet ? getElementSymbol(symbolSet) : null
      const symbol = _.isEmpty(definedSymbol) ? defaultSymbol : definedSymbol

      switch (geometry.type) {
        case "MultiPolygon":
        case "Polygon": {
          geometry?.updateSymbol(symbol)
          break
        }
        default:
          break
      }

      switch (feature.feature_type) {
        case "amenity":
          const highlightedAmenityMarker =
            definedSymbol || getHighlightMarkerSymbol()
          geometry?.updateSymbol(highlightedAmenityMarker)
          break
        case "occupant": {
          switch (feature.properties.category) {
            case "currencyexchange":
            case "donationcenter":
            case "postoffice":
              const highlightedAmenityMarker =
                definedSymbol || getHighlightMarkerSymbol()
              geometry?.updateSymbol(highlightedAmenityMarker)
              break
            default:
              if (feature.properties.render_type === "Logo") {
                this.hideGeometryByElementId(feature.id)
              }
              createHighlightOccupantMarker(feature, {
                extrudeConfig,
                symbol: definedSymbol,
              }).addTo(hilightLayer)
              break
          }
          break
        }
        case "opening":
          break
        default:
          if (defaultMarker) createMarker(feature).addTo(hilightLayer)
          break
      }

      /* Hide the occupant marker before display the highlight marker */
      // const occupantId = IndexedOccupantMarkers[feature.id]
      // if (occupantId) {
      //   hideOccupantMarker(occupantId)
      // }
    })

    // Store new hilightIds to prev ref.
    this.#highlightElementIds = targetElementIds

    if (elementToHilights.length === 0) return
    return featureCollection(
      elementToHilights.map(({ feature }) => {
        const { geometry } = feature
        if (feature.feature_type === "occupant")
          return turfFeature(feature?.properties?.anchor?.geometry)
        return turfFeature(geometry)
      })
    )
  }

  clearHighlightElements() {
    /**
     * Clear previous hilights
     */
    this.#clearAllElementOnLayerByName("hilight")

    //Return geometry to defaulySymbol
    _(this.#highlightElementIds)
      .map((elemId) => this.#elements[elemId]?.geometry)
      .compact()
      .forEach((geometry) => {
        try {
          const defaultSymbol = geometry.options.defaultSymbol
          geometry.updateSymbol(defaultSymbol)
        } catch (err) {
          console.log(
            `error cannot return to defaultSymbol, check if "defaultySymbol" exists in element creation function`
          )
        }
      })
    this.#highlightElementIds = []
  }

  setHighlightedObject(targetObjectIds) {
    const { getHilighPolygonalSymbol } = this.#styler
    const objects = this.threeLayer?.getBaseObjects()
    const objectsToHighlight = objects.filter(({ properties }) =>
      targetObjectIds.includes(properties?.id)
    )
    const { polygonFill: highlightColor } = getHilighPolygonalSymbol("Polygon")
    objectsToHighlight.forEach((obj) => {
      //highlight extruded polygon
      if (obj.type === "ExtrudePolygon") {
        obj.object3d.material.color = new THREE.Color(highlightColor)
      }
      //highlight amenity marker
      if (obj instanceof Marker3d) {
        const { position } = obj.object3d
        const { z } = position
        const tween = new TWEEN.Tween({ z })
          .to({ z: z + 0.02 }, 500)
          .onUpdate((coord) => {
            obj.object3d.position.z = coord.z
          })
        tween.start().repeat(Infinity).yoyo(true)
        obj.object3d.scale.set(1.25, 1.25, 1.25)
      }
    })

    this.#highlightObjectIds = targetObjectIds
  }

  clearHighlightObject() {
    const objects = this.threeLayer?.getBaseObjects() || []
    const highlightedObjs = this.#highlightObjectIds
    const objectsToSetDefault = objects.filter(({ properties }) =>
      highlightedObjs.includes(properties?.id)
    )

    objectsToSetDefault.forEach((obj) => {
      const objectDefaultColor = _.get(obj, "properties.defaultColor")
      if (obj.type === "ExtrudePolygon") {
        obj.object3d.material.color = new THREE.Color(objectDefaultColor)
      }
      if (obj instanceof Marker3d) {
        obj.object3d.scale.set(1, 1, 1)
        const defaultPosition = obj.properties.default.position
        obj.object3d.position.copy(defaultPosition)
      }
    })

    //remove all TWEEN.js animation
    TWEEN.removeAll()

    this.#highlightObjectIds = []
  }

  /**
   * User Location
   ****************************/
  // TODO: To consider if we should use setter `set userLocation` instead?

  addUserLocation(value) {
    // TODO: Check why this if exists
    if (
      this.#userLocationGeometry &&
      value.id === this.#userLocationGeometry?.id
    )
      return
    const { createUserLocationMarker } = this.#styler

    this.#userLocationGeometry = value

    const { properties, feature_type } = value

    // If UserLocationGeometry not exists, create one.

    const markerLayer = this.map.getLayer(`user_location`)
    if (!this.#userLocationElement?.geometry && markerLayer) {
      const geometry = createUserLocationMarker(value)
      geometry.addTo(markerLayer)

      // Add marker to this.#elements
      const element = {
        geometry,
        properties,
        feature_type,
        feature: value,
      }
      this.#elements["user_location"] = element
      this.#userLocationElement = element
    }
  }

  removeUserLocation() {
    this.#userLocationGeometry = null
    if (this.#userLocationElement) {
      const { geometry } = this.#userLocationElement
      if (geometry) geometry.remove()

      this.#userLocationElement = null
      this.#elements["user_location"] = null
    }
  }

  showUserLocationMarker() {
    this.#userLocationElement?.geometry.show()
  }

  hideUserLocationMarker() {
    this.#userLocationElement?.geometry.hide()
  }

  addLastUserLocation(value) {
    const newLastLocationId = value?.id
    // If the last location ID is undefined or exists, no new marker will be added.
    const isExists =
      this.#lastUserLocationGeometries.findIndex(
        ({ feature }) => feature?.id === newLastLocationId
      ) >= 0

    if (!newLastLocationId || isExists) return

    const { createLastUserLocationMarker } = this.#styler

    this.#lastUserLocationGeometries = [
      ...this.#lastUserLocationGeometries,
      value,
    ]

    const { properties, feature_type } = value

    const markerLayer = this.map.getLayer(`last_user_location`)

    const geometry = createLastUserLocationMarker(value)
    if (!geometry) return
    geometry.addTo(markerLayer)

    // Add marker to this.#elements
    const element = {
      geometry,
      properties,
      feature_type,
      feature: value,
    }
    this.#elements[`last_user_location-${properties?.id}`] = element
    this.#lastUserLocationElements = [
      ...this.#lastUserLocationElements,
      element,
    ]
  }

  removeLastUserLocation() {
    this.#lastUserLocationGeometries = []
    if (
      this.#lastUserLocationElements &&
      this.#lastUserLocationElements.length > 0
    ) {
      for (const lastUserLocationElem of this.#lastUserLocationElements) {
        const { geometry, properties = {} } = lastUserLocationElem
        if (geometry) {
          geometry.remove()
          delete this.#elements[`last_user_location-${properties?.id}`]
        }
      }
      this.#lastUserLocationElements = []
    }
  }

  hideLastUserLocationMarker() {
    for (const lastUserLocationElem of this.#lastUserLocationElements) {
      const { geometry } = lastUserLocationElem
      if (geometry) geometry.hide()
    }
  }

  /**
   * render (frame)
   */
  getTargetViewCenter = (
    targetView,
    options = { offset: { top: 0, left: 0, right: 0, bottom: 0 } }
  ) => {
    // Extend the input targetView with default values from the current view if not provided
    const map = this.map
    const { offset } = options
    const { top = 0, left = 0, right = 0, bottom = 0 } = offset
    // Temporarily set the map to the target view to calculate the extent.
    // This approach requires that the map view can be adjusted without triggering a visible change.
    const originalState = {
      bearing: map.getBearing(),
      center: map.getCenter(),
      pitch: map.getPitch(),
      zoom: map.getZoom(),
    }

    const finalView = {
      bearing: _.isNil(targetView.bearing)
        ? map.getBearing()
        : targetView.bearing,
      center: _.isNil(targetView.center) ? map.getCenter() : targetView.center,
      pitch: _.isNil(targetView.pitch) ? map.getPitch() : targetView.pitch,
      zoom: _.isNil(targetView.zoom) ? map.getZoom() : targetView.zoom,
    }

    map.setView(finalView)

    // Calculate the visible extent at this state
    const projectedTargetCenter = map
      .coordinateToContainerPoint(finalView.center)
      .add(right / 2 - left / 2, bottom / 2 - top / 2)
    const adjustedTargetCenter = map.containerPointToCoordinate(
      projectedTargetCenter
    )
    // Reset the map to its original state to avoid any visible changes
    map.setView(originalState)
    return adjustedTargetCenter
  }

  /**
   * END of User Location
   ****************************/

  showGeometryByElementId = (elementId) => {
    const geometry = _.get(this.#elements, `${elementId}.geometry`)
    if (geometry) geometry.show()
  }

  hideGeometryByElementId = (elementId) => {
    const geometry = _.get(this.#elements, `${elementId}.geometry`)
    if (geometry) geometry.hide()
  }

  setFeatureObject3DsOpacity = (opacity = 1) => {
    const objects = this.threeLayer?.getBaseObjects()
    objects?.forEach((baseObject) => {
      baseObject.getObject3d().traverse((child) => {
        if (child.isMesh === true) {
          child.material.opacity = opacity
        }
      })
    })
  }

  /**
   * Navigation
   ****************************/
  createNavigationGeometries = (stepGeometries) => {
    const { createOriginMarker, createDestinationMarker, createStepPath } =
      this.#styler
    const routePathLayer = this.map.getLayer(`route.path`)
    const routeMarkerLayer = this.map.getLayer(`route.marker`)
    stepGeometries.forEach((stepGeometry) => {
      const { geometry, properties, feature_type = null } = stepGeometry

      let stepElement
      try {
        switch (geometry.type) {
          // Create a destination marker and route path
          case "Point":
            switch (feature_type) {
              case "origin-marker":
                stepElement =
                  createOriginMarker(stepGeometry).addTo(routeMarkerLayer)
                break
              case "destination-marker":
                stepElement =
                  createDestinationMarker(stepGeometry).addTo(routeMarkerLayer)
                break
              default:
                break
            }
            break
          case "LineString":
            stepElement = createStepPath(stepGeometry).addTo(routePathLayer)
            break
          default:
            break
        }
        if (stepElement) {
          // Add tot Elements object
          this.#elements[stepGeometry.id] = {
            geometry: stepElement,
            properties: { ...stepGeometry, ...properties },
          }
          //
        }
      } catch (err) {
        // console.log(err, {
        //   index: index,
        //   stepGeometries,
        // })
        // throw err;
        console.log(err)
      }
    })
  }

  createOverviewStepPathByOrdinal = (stepGeometries, viewingOrdinal) => {
    const { createLineStringFromGeometries } = this.#styler
    const initialStepGeometries = stepGeometries
      .filter(({ properties }) => properties.ordinal === viewingOrdinal)
      .map(({ geometry }) => geometry)
    return createLineStringFromGeometries(initialStepGeometries)
  }

  getRoutePathGeometryById = (id) => {
    const routePathLayer = this.map.getLayer(`route.path`)
    const geometry = routePathLayer.getGeometryById(id)
    if (!geometry) return
    return geometry
  }

  clearNavigationGeometries() {
    const routePathLayer = this.map.getLayer(`route.path`)
    const routeMarkerLayer = this.map.getLayer(`route.marker`)

    if (routePathLayer) {
      routePathLayer.forEach((geometry) => {
        const id = geometry.getId()
        this.#elements[id] = null
      })
    }

    if (routeMarkerLayer) {
      routeMarkerLayer.forEach((geometry) => {
        const id = geometry.getId()
        this.#elements[id] = null
      })
    }
    this.#clearAllElementOnLayerByName("route.path")
    this.#clearAllElementOnLayerByName("route.marker")
  }
  /**
   * END of Navigation
   ****************************/

  /**
   * hide/show venue 3dmodel
   **/

  hideVenueObjects = () => {
    this.showVenueObject = false
  }
  showVenueObjects = () => {
    this.showVenueObject = true
  }

  /**
   * Other functions
   */

  enableClick = () => this.map.config({ geometryEvents: true })
  disableClick = () => this.map.config({ geometryEvents: false })

  freeze = () => this.map.config({ zoomable: false, draggable: false })
  unfreeze = () => this.map.config({ zoomable: true, draggable: true })

  /**
   * render (frame)
   */

  render() {
    // TODO: Update User Location marker position

    this.threeLayer._needsUpdate = !this.threeLayer._needsUpdate
    if (this.threeLayer._needsUpdate) {
      this.threeLayer.redraw()
    }

    if (this.threeLayer) {
      const objectOpacity = _.clamp(38 - 2 * this.#zoomLevel, 0, 1)
      this.#objects.forEach((object) => {
        object.getObject3d().traverse((child) => {
          if (child.isMesh) child.material.opacity = objectOpacity
          //TODO: recheck this line if it actually works
          /** set Mesh visible = false when opacity is 0
           * and true when opacity > 0
           */
          child.visible = !!objectOpacity
        })
        object.getObject3d().visible = !!objectOpacity
      })

      if (this.#billboardObjects) {
        this.#billboardObjects.forEach((object) => {
          const objectScale = _.clamp(20 - 1 * this.#zoomLevel, 1, 1.05)
          object.getObject3d().scale.set(objectScale, objectScale, 1)
        })
      }

      const venueOpacity = objectOpacity < 0.3 ? 0 : objectOpacity
      this.#venueObjects.forEach((object) => {
        object.getObject3d().traverse((child) => {
          if (child.isMesh) child.material.opacity = venueOpacity
          /** set Mesh visible = false when opacity is 0
           * and true when opacity > 0
           */
          child.visible = this.showVenueObject && !!venueOpacity
        })
      })

      // Update bearing to each GroundLabel to determine if text should flip or not
      const view = this.map.getView()
      this.#groundObjects.forEach((gLabel) => {
        gLabel.bearing = view.bearing
      })

      // Update the rotation around the z-axis of the 3D marker to keep it facing the camera.
      const currBearing = this.map.getBearing()
      const radianTowardCamera = Math.abs(
        THREE.MathUtils.degToRad(
          currBearing > 180 ? currBearing : 360 - currBearing
        )
      )
      this.#marker3DObjects.forEach((object) => {
        object.getObject3d().rotation.z = radianTowardCamera
      })
    }
    //update TWEEN.js animaton
    TWEEN.update()
    requestAnimationFrame(this.render.bind(this))
  }
}
